// src/auth/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

// Normaliza API_BASE (permite vacío para mismo origen) y evita doble '/'
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const api = (path = "") => {
  const p = String(path || "");
  const suf = p.startsWith("/") ? p : `/${p}`;
  return `${API_BASE}${suf}`;
};


const AuthContext = createContext();

const isAdminPath = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

const LS_ADMIN = "admin_user";
const LS_WEB = "web_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // { id, name, email, role, scope: 'admin'|'web' }

  useEffect(() => {
    (async () => {
      try {
        // 1) Intentar ADMIN solo si estás en /admin
        if (isAdminPath()) {
          const ra = await fetch(api("/admin/me"), { credentials: "include" });
          const ja = await ra.json().catch(() => ({}));
          if (ja?.ok && ja.user) {
            setUser({ ...ja.user, scope: "admin" });
            localStorage.setItem(LS_ADMIN, JSON.stringify({ ...ja.user, scope: "admin" }));
            return;
          }
        }
        // 2) Intentar WEB
        const rw = await fetch(api("/api/auth/web/me"), { credentials: "include" });
        const jw = await rw.json().catch(() => ({}));
        if (jw?.ok && jw.user) {
          setUser({ ...jw.user, scope: "web" });
          localStorage.setItem(LS_WEB, JSON.stringify({ ...jw.user, scope: "web" }));
          return;
        }
        // 3) Fallback a localStorage (según path)
        const lsKey = isAdminPath() ? LS_ADMIN : LS_WEB;
        const raw = localStorage.getItem(lsKey);
        if (raw) setUser(JSON.parse(raw));
      } catch {
        // último recurso: cualquiera que exista
        const rawA = localStorage.getItem(LS_ADMIN);
        const rawW = localStorage.getItem(LS_WEB);
        if (rawA) setUser(JSON.parse(rawA));
        else if (rawW) setUser(JSON.parse(rawW));
      }
    })();
  }, []);

  // Guarda según scope (estable con useCallback)
  const login = useCallback((userData) => {
    const scope = userData?.scope === "admin" ? "admin" : "web";
    setUser({ ...userData, scope });
    const key = scope === "admin" ? LS_ADMIN : LS_WEB;
    localStorage.setItem(key, JSON.stringify({ ...userData, scope }));
  }, []);

  const logout = useCallback((scope) => {
    const s = scope ?? user?.scope;
    setUser(null);
    const key = s === "admin" ? LS_ADMIN : LS_WEB;
    localStorage.removeItem(key);
  }, [user?.scope]);

  const isAuthenticated = !!user;
  const value = useMemo(() => ({ user, isAuthenticated, login, logout }), [user, isAuthenticated, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
